import React, { useEffect, useState } from 'react';
import { losApi } from './losApi';
import { downloadWithAuth } from './api';
import { Input } from '../components/ui/input';
import { TableScroller } from '@/components/ui/table-scroller';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { FileBarChart2, Download, Plus, Loader2, RefreshCw, Lock, Pencil } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { api } from './api';
import { errorMessage } from './errors';
import DialogShell from './kit/DialogShell';
import MoneyInput from './shared/MoneyInput';
import NumberInput from './shared/NumberInput';

const fmt$ = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtN = (n) => Number(n || 0).toLocaleString('en-US');

// The RMLA lines a state row carries: `<key>_count` and `<key>_amount` on the report.
const ACTIVITY_LINES = [
  ['apps_received', 'Applications received'],
  ['closed', 'Closed / funded'],
  ['denied', 'Denied'],
  ['withdrawn', 'Withdrawn'],
  ['approved_not_accepted', 'Approved, not accepted'],
  ['preapp_no_app', 'Pre-approved, no application'],
  ['closed_purchase', 'Closed: purchase'],
  ['closed_refinance', 'Closed: refinance'],
  ['closed_home_improvement', 'Closed: home improvement'],
  ['closed_other', 'Closed: other purpose'],
  ['closed_conventional', 'Closed: conventional'],
  ['closed_fha', 'Closed: FHA'],
  ['closed_va', 'Closed: VA'],
  ['closed_usda', 'Closed: USDA'],
  ['closed_jumbo', 'Closed: jumbo'],
];
const LINE_LABELS = Object.fromEntries(ACTIVITY_LINES.flatMap(([k, label]) => [[`${k}_count`, `${label} (count)`], [`${k}_amount`, `${label} ($)`]]));
const overriddenKeys = (s) => Object.keys(s.manual_overrides || {});
const NAME_REQUIRED = "Enter the company's legal name";

export default function MCRPage() {
  const [reports, setReports] = useState([]);
  const [active, setActive] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [exporting, setExporting] = useState(null); // 'csv' | 'xml' while that file is being fetched

  const load = () => losApi.listMcr().then((rs) => {
    setReports(rs);
    if (rs.length && !active) losApi.getMcr(rs[0].mcr_id).then(setActive);
  });
  useEffect(() => { load(); }, []);

  const openReport = (id) => losApi.getMcr(id).then(setActive);
  // Only a draft can change; a final or submitted report is read-only.
  const draft = active?.status === 'draft';
  const refresh = async () => {
    if (!active) return;
    try {
      const r = await losApi.refreshMcr(active.mcr_id); setActive(r); toast({ title: 'Refreshed from loan pipeline' });
    } catch (e) { toast({ title: 'Failed', description: errorMessage(e, 'Error') }); }
  };
  const finalize = async () => {
    if (!active) return;
    if (!(await confirmDialog({ title: 'Mark this MCR as final?', description: 'You can still export after finalization.', confirmText: 'Finalize' }))) return;
    const r = await losApi.finalizeMcr(active.mcr_id); setActive(r); toast({ title: 'MCR finalized' });
  };
  const exportFile = async (kind) => {
    if (kind === 'xml' && !(active.entity_name || '').trim()) { toast({ title: NAME_REQUIRED }); return; }
    setExporting(kind);
    try {
      const path = kind === 'csv' ? losApi.mcrCsvPath(active.mcr_id) : losApi.mcrXmlPath(active.mcr_id);
      await downloadWithAuth(path, `MCR_${active.year}_${active.quarter}.${kind}`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl font-bold flex items-center gap-2" style={{ color: '#070B5F' }}>
            <FileBarChart2 /> Mortgage Call Reports
          </h1>
          <p className="text-slate-500 mt-1">NMLS quarterly RMLA + Financial Condition — all 50 states, auto-populated from your loan pipeline.</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="new-mcr-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white font-semibold" style={{ background: '#6BA82E' }}>
          <Plus size={14} /> New Quarter
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 min-w-0 bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Reports</div>
          <div className="space-y-1">
            {reports.map((r) => (
              <button key={r.mcr_id} onClick={() => openReport(r.mcr_id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm ${active?.mcr_id === r.mcr_id ? 'bg-slate-100 font-semibold' : 'hover:bg-slate-50'}`}
                data-testid={`mcr-report-${r.year}-${r.quarter}`}
                style={{ color: '#070B5F' }}>
                {r.year} {r.quarter}
                <span className="text-xs text-slate-400 ml-2">{r.status}</span>
              </button>
            ))}
            {reports.length === 0 && <div className="text-xs text-slate-400 italic px-3">No reports yet.</div>}
          </div>
        </div>

        {active && (
          <div className="lg:col-span-3 min-w-0 space-y-4">
            <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="font-serif text-xl font-bold" style={{ color: '#070B5F' }}>{active.year} {active.quarter} — {active.entity_name || 'Report'}</div>
                <div className="text-xs text-slate-500 mt-1">NMLS ID: {active.nmls_id || '—'} · Status: <span className="font-semibold">{active.status}</span> · {active.state_activities.length} state(s) with activity</div>
              </div>
              <div className="flex gap-2">
                {draft && (
                  <button onClick={refresh} className="text-xs font-semibold px-3 py-2 rounded-lg border border-slate-200 inline-flex items-center gap-1.5" data-testid="refresh-mcr"><RefreshCw size={12} /> Refresh</button>
                )}
                {active.status !== 'final' && (
                  <button onClick={finalize} className="text-xs font-semibold px-3 py-2 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: '#070B5F' }} data-testid="finalize-mcr"><Lock size={12} /> Finalize</button>
                )}
                <button onClick={() => exportFile('csv')} disabled={!!exporting} className="text-xs font-semibold px-3 py-2 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: '#6BA82E' }} data-testid="mcr-csv">{exporting === 'csv' ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} CSV</button>
                <button onClick={() => exportFile('xml')} disabled={!!exporting} className="text-xs font-semibold px-3 py-2 rounded-lg border border-slate-200 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="mcr-xml">{exporting === 'xml' ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} XML</button>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
              <div className="font-serif font-semibold mb-3" style={{ color: '#070B5F' }}>Residential Mortgage Loan Activity (RMLA)</div>
              {active.state_activities.length === 0 ? (
                <div className="text-sm text-slate-400 italic py-6 text-center">No activity in this quarter across your loan pipeline. Loans are auto-included when their property state, application date, or funding date falls in this quarter.</div>
              ) : (
                <TableScroller>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
                        <th className="pb-2 pr-2 min-w-[3.5rem]">State</th>
                        <th className="pb-2 pr-2 text-right min-w-[3.5rem]">Apps</th>
                        <th className="pb-2 pr-2 text-right min-w-[4.5rem]">Apps $</th>
                        <th className="pb-2 pr-2 text-right min-w-[4.5rem]">Closed</th>
                        <th className="pb-2 pr-2 text-right min-w-[4.5rem]">Closed $</th>
                        <th className="pb-2 pr-2 text-right">Denied</th>
                        <th className="pb-2 pr-2 text-right">Withdrawn</th>
                        <th className="pb-2 pr-2 text-right">Auto?</th>
                        <th className="pb-2"><span className="sr-only">Edit</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {active.state_activities.map((s) => (
                        <tr key={s.state} className="border-b border-slate-50" data-testid={`mcr-state-${s.state}`}>
                          <td className="py-2 pr-2 font-bold" style={{ color: '#070B5F' }}>{s.state}</td>
                          <td className="py-2 pr-2 text-right">{fmtN(s.apps_received_count)}</td>
                          <td className="py-2 pr-2 text-right">{fmt$(s.apps_received_amount)}</td>
                          <td className="py-2 pr-2 text-right">{fmtN(s.closed_count)}</td>
                          <td className="py-2 pr-2 text-right font-semibold" style={{ color: '#6BA82E' }}>{fmt$(s.closed_amount)}</td>
                          <td className="py-2 pr-2 text-right">{fmtN(s.denied_count)}</td>
                          <td className="py-2 pr-2 text-right">{fmtN(s.withdrawn_count)}</td>
                          <td className="py-2 pr-2 text-right">
                            <span className="text-[9px] uppercase font-bold" style={{ color: s.auto_populated && !overriddenKeys(s).length ? '#6BA82E' : '#f59e0b' }}
                              title={overriddenKeys(s).length ? `Overridden: ${overriddenKeys(s).map((k) => LINE_LABELS[k] || k).join(', ')}` : undefined}
                              data-testid={`mcr-state-${s.state}-source`}>
                              {overriddenKeys(s).length ? 'overridden' : s.auto_populated ? 'auto' : 'manual'}
                            </span>
                          </td>
                          <td className="py-2 text-right">
                            <button onClick={() => setEditing(s)} disabled={!draft} aria-label={`Edit ${s.state} numbers`}
                              className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-40" data-testid={`mcr-edit-${s.state}`}>
                              <Pencil size={12} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroller>
              )}
            </div>

            <FinancialConditionEditor mcr={active} onSaved={(r) => setActive(r)} />
          </div>
        )}
      </div>

      {editing && <StateOverrideDialog mcr={active} row={editing} onClose={() => setEditing(null)} onSaved={(r) => { setActive(r); setEditing(null); }} />}
      {showNew && <NewMCRModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function FinancialConditionEditor({ mcr, onSaved }) {
  const [fc, setFc] = useState(mcr.financial_condition || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => setFc(mcr.financial_condition || {}), [mcr.mcr_id]);
  const upd = (k, v) => setFc(f => ({ ...f, [k]: Number(v) || 0 }));

  const save = async () => {
    setSaving(true);
    try { const r = await losApi.updateFc(mcr.mcr_id, fc); onSaved(r); toast({ title: 'Financial Condition saved' }); }
    catch (e) { toast({ title: 'Failed', description: errorMessage(e, 'Error') }); }
    finally { setSaving(false); }
  };
  const editable = mcr.status === 'draft';

  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
      <div className="font-serif font-semibold mb-3" style={{ color: '#070B5F' }}>Financial Condition (entity-wide, entered from GL)</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <FCFld label="Cash & Equivalents" value={fc.cash_and_equivalents} onChange={(v) => upd('cash_and_equivalents', v)} disabled={!editable} />
        <FCFld label="Loans Held for Sale" value={fc.loans_held_for_sale} onChange={(v) => upd('loans_held_for_sale', v)} disabled={!editable} />
        <FCFld label="Total Assets" value={fc.total_assets} onChange={(v) => upd('total_assets', v)} disabled={!editable} />
        <FCFld label="Total Liabilities" value={fc.total_liabilities} onChange={(v) => upd('total_liabilities', v)} disabled={!editable} />
        <FCFld label="Total Equity" value={fc.total_equity} onChange={(v) => upd('total_equity', v)} disabled={!editable} />
        <FCFld label="Total Revenue" value={fc.total_revenue} onChange={(v) => upd('total_revenue', v)} disabled={!editable} />
        <FCFld label="Total Expenses" value={fc.total_expenses} onChange={(v) => upd('total_expenses', v)} disabled={!editable} />
        <FCFld label="Net Income" value={fc.net_income} onChange={(v) => upd('net_income', v)} disabled={!editable} />
      </div>
      <button onClick={save} disabled={saving || !editable} className="mt-4 text-xs font-semibold px-4 py-2 rounded-lg text-white flex items-center gap-1.5 disabled:opacity-50" style={{ background: '#070B5F' }} data-testid="save-fc">
        {saving ? <Loader2 size={12} className="animate-spin" /> : null} Save Financial Condition
      </button>
    </div>
  );
}
function FCFld({ label, value, onChange, disabled }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-slate-500">{label}</Label>
      <MoneyInput cents value={value ?? 0} onChange={(n) => onChange(n ?? 0)} disabled={disabled} className="mt-1 text-sm" />
    </div>
  );
}

// Edit one state row's RMLA numbers. Only the lines that changed are sent: every
// sent number becomes a manual override that a later refresh keeps. A cleared
// input sends null, which removes that line's override (back to the calculated number).
function StateOverrideDialog({ mcr, row, onClose, onSaved }) {
  const keys = ACTIVITY_LINES.flatMap(([k]) => [`${k}_count`, `${k}_amount`]);
  const [vals, setVals] = useState(() => Object.fromEntries(keys.map((k) => [k, row[k] ?? 0])));
  const [saving, setSaving] = useState(false);
  const overrides = row.manual_overrides || {};
  const locked = mcr.status !== 'draft';
  const changes = {};
  keys.forEach((k) => {
    const v = vals[k];
    if (v === null) { if (k in overrides) changes[k] = null; }
    else if (Number(v) !== Number(row[k] ?? 0)) changes[k] = Number(v);
  });
  const changed = Object.keys(changes).length > 0;
  const set = (k) => (n) => setVals((v) => ({ ...v, [k]: n }));
  const lineKeys = (k) => [`${k}_count`, `${k}_amount`];
  const overriddenIn = (k) => lineKeys(k).filter((x) => x in overrides);
  const reset = (k) => setVals((v) => ({ ...v, ...Object.fromEntries(overriddenIn(k).map((x) => [x, null])) }));
  const calc = row.calculated || {};

  const save = async () => {
    if (Object.values(changes).some((n) => n !== null && n < 0)) { toast({ title: "Numbers can't be negative" }); return; }
    setSaving(true);
    try {
      await losApi.overrideState(mcr.mcr_id, row.state, changes);
      onSaved(await losApi.getMcr(mcr.mcr_id));
      toast({ title: `${row.state} numbers saved` });
    } catch (e) { toast({ title: 'Failed', description: errorMessage(e, 'Error') }); }
    finally { setSaving(false); }
  };

  return (
    <DialogShell open onOpenChange={(o) => { if (!o) onClose(); }} size="lg" title={`Edit ${row.state} numbers`}
      description="Lines you change stay fixed when you refresh from the loan pipeline. Clear a line to go back to the calculated number."
      data-testid="mcr-override-dialog"
      footer={(
        <>
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={saving || !changed || locked} className="text-sm px-4 py-2 rounded-lg text-white bg-brand-accent disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="mcr-override-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : null} Save numbers
          </button>
        </>
      )}>
      <div className="grid grid-cols-[1fr_6rem_9rem] gap-2 items-center text-sm">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Line</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 text-right">Count</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 text-right">Amount</div>
        {ACTIVITY_LINES.map(([k, label]) => {
          const over = overriddenIn(k);
          const pending = over.length > 0 && over.every((x) => vals[x] === null);
          const [c, a] = lineKeys(k).map((x) => calc[x]);
          return (
            <React.Fragment key={k}>
              <div className="text-brand-navy" data-testid={`mcr-override-${k}-label`}>
                <div className={over.length ? 'font-semibold' : ''}>{label}</div>
                {over.length > 0 && (
                  <div className="text-[11px] font-normal text-amber-600" data-testid={`mcr-override-${k}-mark`}>
                    Overridden{c != null && a != null ? ` · calculated ${fmtN(c)} / ${fmt$(a)}` : ''}
                    {pending ? ' · back to calculated on save' : (
                      <button type="button" onClick={() => reset(k)} disabled={locked} className="ml-2 underline text-brand-accent disabled:opacity-50" data-testid={`mcr-reset-${k}`}>Reset to calculated</button>
                    )}
                  </div>
                )}
              </div>
              <NumberInput integer value={vals[`${k}_count`]} onChange={set(`${k}_count`)} disabled={locked} aria-label={`${label} count`} data-testid={`mcr-override-${k}_count`} />
              <MoneyInput cents value={vals[`${k}_amount`]} onChange={set(`${k}_amount`)} disabled={locked} aria-label={`${label} amount`} data-testid={`mcr-override-${k}_amount`} />
            </React.Fragment>
          );
        })}
      </div>
    </DialogShell>
  );
}

function NewMCRModal({ onClose, onCreated }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState('Q' + Math.min(4, Math.ceil((now.getMonth() + 1) / 3)));
  const [entity, setEntity] = useState('');
  const [nmls, setNmls] = useState('');
  const [saving, setSaving] = useState(false);
  // Start from the workspace name; a name typed before it arrives wins.
  useEffect(() => {
    let live = true;
    api.get('/tenants/mine').then(({ data }) => { if (live && data?.name) setEntity((cur) => cur || data.name); }).catch(() => {});
    return () => { live = false; };
  }, []);
  const submit = async () => {
    setSaving(true);
    try { await losApi.createMcr({ year: Number(year), quarter, entity_name: entity, nmls_id: nmls }); onCreated(); }
    catch (e) { toast({ title: 'Failed', description: errorMessage(e, 'Error') }); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogTitle className="font-serif text-lg font-bold mb-4" style={{ color: '#070B5F' }}>New MCR</DialogTitle>
        <div className="space-y-3">
          <div><Label className="text-xs">Year</Label><NumberInput integer value={year} onChange={(n) => setYear(n ?? '')} className="mt-1" /></div>
          <div><Label className="text-xs">Quarter</Label>
            <Select value={quarter} onValueChange={setQuarter}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{['Q1','Q2','Q3','Q4'].map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Entity Name</Label><Input value={entity} onChange={(e) => setEntity(e.target.value)} className="mt-1" data-testid="new-mcr-entity" /></div>
          <div><Label className="text-xs">NMLS ID</Label><Input value={nmls} onChange={(e) => setNmls(e.target.value)} className="mt-1" /></div>
        </div>
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={saving} className="text-sm px-4 py-2 rounded-lg text-white" style={{ background: '#6BA82E' }} data-testid="submit-new-mcr">Create & Auto-Populate</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
